import { Box, Button, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material'
import translate from '../../../i18n/translate'
import toast from 'react-hot-toast'
import { useIntl } from 'react-intl'
import { Formik, Form } from 'formik'
import { FIELDS, useServiceValidationSchema } from './useValidationSchema'
import { organizerApi } from '../../../modules/services/organizerService'
import { useDebounce } from 'react-use'
import { useState } from 'react'

type ServiceFormViewProps = {
  userId: number;
  event: any
  onCloseModal: () => void
}

export default function ServiceFormView({
  userId,
  event,
  onCloseModal,
}: ServiceFormViewProps) {
  const intl = useIntl()

  const [vals, setVals] =  useState<any>({})
  const [isOverdraftConfirmed, setIsOverdraftConfirmed] =  useState<boolean>(false)

  const [
    createOrganizerEventTransaction,
    { isLoading }
  ] = organizerApi.useCreateOrganizerEventTransactionMutation()

  const [
    calculateOrganizerEventTransaction,
    { isLoading: isCalculating, data: calculateData }
  ] = organizerApi.useCalculateOrganizerEventTransactionMutation()

  const { data: organizerEventData } = organizerApi.useGetOrganizerEventInfoQuery({ userId, eventLinkId: event?.eventLink }, { skip: !event });

  const handleSubmit = async (values: any) => {
    try {
      if (userId) {
        const data = {
          name: values?.[FIELDS.NAME] || null,
          amount: null,
          feeAmount: values?.[FIELDS.FEE_AMOUNT] || null,
          fastWithdrawalFeeAmount: values?.[FIELDS.FAST_WITHDRAWAL_FEE_AMOUNT] || null,
          adminWithdrawalFeeAmount: values?.[FIELDS.ADMIN_WITHDRAWAL_FEE_AMOUNT] || null,
          adminNote: values?.[FIELDS.ADMIN_NOTE] || null,
          billAccountId: null,
        }

        await createOrganizerEventTransaction({
          userId: userId,
          eventLinkId: event.eventLink,
          data
        });

      }
      onCloseModal()
      toast.success(translate('successTransactionCreateMessage'))
    } catch (error) {
      toast.error(translate('errorGenericMessage'))
    }
  }

  useDebounce(
    async () => {
      if (vals?.[FIELDS.FEE_AMOUNT] || vals?.[FIELDS.FAST_WITHDRAWAL_FEE_AMOUNT] || vals?.[FIELDS.ADMIN_WITHDRAWAL_FEE_AMOUNT]) {
        await calculateOrganizerEventTransaction({
          userId: userId,
          eventLinkId: event.eventLink,
          data: {
            feeAmount: vals?.[FIELDS.FEE_AMOUNT] || null,
            fastWithdrawalFeeAmount: vals?.[FIELDS.FAST_WITHDRAWAL_FEE_AMOUNT] || null,
            adminWithdrawalFeeAmount: vals?.[FIELDS.ADMIN_WITHDRAWAL_FEE_AMOUNT] || null,
          }
        });
      }
      setIsOverdraftConfirmed(false)
    },
    750,
    [vals?.[FIELDS.FEE_AMOUNT], vals?.[FIELDS.FAST_WITHDRAWAL_FEE_AMOUNT], vals?.[FIELDS.ADMIN_WITHDRAWAL_FEE_AMOUNT]]
  );

  const validationSchema = useServiceValidationSchema()

  return (
    <Formik
      initialValues={{
        [FIELDS.NAME]: null,
        [FIELDS.FEE_AMOUNT]: null,
        [FIELDS.ADMIN_WITHDRAWAL_FEE_AMOUNT]: null,
        [FIELDS.FAST_WITHDRAWAL_FEE_AMOUNT]: null,
        [FIELDS.ADMIN_NOTE]: null,
      }}
      onSubmit={handleSubmit}
      enableReinitialize
      validationSchema={validationSchema}
    >
      {({ errors, values, touched, handleBlur, handleChange, isValid }) => {
        const onChange = (e: any) => {
          handleChange(e);
          setVals({ ...vals, [e.target.name]: e.target.value ? Number(e.target.value) : null });
        }

        return (
          <Form>
            <Box
              p={2}
              display='flex'
              flexDirection='column'
              justifyContent='center'
              width={1}
              alignItems='center'
              gap={2}
            >
              <Box>
                <Typography variant='h6'>
                  {intl.formatMessage({ id: 'selectedEventName' })}: {event?.name}
                </Typography>
                {organizerEventData ? (
                  <Box>
                    <Typography>
                      {intl.formatMessage({ id: 'totalEventAmount' })}: {intl.formatNumber(organizerEventData?.totalEventAmount)}₪
                    </Typography>
                    <Typography>
                      {intl.formatMessage({ id: 'totalWithdrawnAmount' })}: {intl.formatNumber(organizerEventData?.totalWithdrawnAmount)}₪
                    </Typography>
                    <Typography>
                      {intl.formatMessage({ id: 'availableForWithdrawalAmount' })}: {intl.formatNumber(organizerEventData?.availableForWithdrawalAmount)}₪
                    </Typography>
                  </Box>
                ) : null}
              </Box>
              <TextField
                name={FIELDS.NAME}
                value={values?.[FIELDS.NAME]}
                error={touched?.[FIELDS.NAME] && Boolean(errors?.[FIELDS.NAME])}
                helperText={touched?.[FIELDS.NAME] && errors?.[FIELDS.NAME]}
                onChange={handleChange}
                onBlur={handleBlur}
                variant='outlined'
                color='warning'
                disabled={isLoading}
                fullWidth
                type='text'
                label={intl.formatMessage({
                  id: 'transactionNameLabel'
                })}
                placeholder={intl.formatMessage({
                  id: 'transactionNameLabel'
                })}
                dir='ltr'
                sx={{
                  '.MuiInputBase-root.MuiInput-root:before': {
                    borderBottom: '0px'
                  },
                  '.MuiInputBase-root.MuiInput-root:hover': {
                    borderBottom: '0px'
                  }
                }}
              />
              <TextField
                name={FIELDS.ADMIN_WITHDRAWAL_FEE_AMOUNT}
                value={values?.[FIELDS.ADMIN_WITHDRAWAL_FEE_AMOUNT]}
                error={touched?.[FIELDS.ADMIN_WITHDRAWAL_FEE_AMOUNT] && Boolean(errors?.[FIELDS.ADMIN_WITHDRAWAL_FEE_AMOUNT])}
                helperText={touched?.[FIELDS.ADMIN_WITHDRAWAL_FEE_AMOUNT] && errors?.[FIELDS.ADMIN_WITHDRAWAL_FEE_AMOUNT]}
                onChange={onChange}
                onBlur={handleBlur}
                variant='outlined'
                color='warning'
                disabled={isLoading}
                fullWidth
                type='number'
                label={intl.formatMessage({ id: 'transactionAdminWithdrawalFeeAmountLabel' })}
                placeholder={intl.formatMessage({
                  id: 'transactionAdminWithdrawalFeeAmountLabel'
                })}
                dir='ltr'
                sx={{
                  '.MuiInputBase-root.MuiInput-root:before': {
                    borderBottom: '0px'
                  },
                  '.MuiInputBase-root.MuiInput-root:hover': {
                    borderBottom: '0px'
                  }
                }}
              />
              <TextField
                name={FIELDS.FEE_AMOUNT}
                value={values?.[FIELDS.FEE_AMOUNT]}
                error={touched?.[FIELDS.FEE_AMOUNT] && Boolean(errors?.[FIELDS.FEE_AMOUNT])}
                helperText={touched?.[FIELDS.FEE_AMOUNT] && errors?.[FIELDS.FEE_AMOUNT]}
                onChange={onChange}
                onBlur={handleBlur}
                variant='outlined'
                color='warning'
                disabled={isLoading}
                fullWidth
                type='number'
                label={intl.formatMessage({ id: 'transactionFeeAmountLabel' })}
                placeholder={intl.formatMessage({
                  id: 'transactionFeeAmountLabel'
                })}
                dir='ltr'
                sx={{
                  '.MuiInputBase-root.MuiInput-root:before': {
                    borderBottom: '0px'
                  },
                  '.MuiInputBase-root.MuiInput-root:hover': {
                    borderBottom: '0px'
                  }
                }}
              />
              <TextField
                name={FIELDS.FAST_WITHDRAWAL_FEE_AMOUNT}
                value={values?.[FIELDS.FAST_WITHDRAWAL_FEE_AMOUNT]}
                error={touched?.[FIELDS.FAST_WITHDRAWAL_FEE_AMOUNT] && Boolean(errors?.[FIELDS.FAST_WITHDRAWAL_FEE_AMOUNT])}
                helperText={touched?.[FIELDS.FAST_WITHDRAWAL_FEE_AMOUNT] && errors?.[FIELDS.FAST_WITHDRAWAL_FEE_AMOUNT]}
                onChange={onChange}
                onBlur={handleBlur}
                variant='outlined'
                color='warning'
                disabled={isLoading}
                fullWidth
                type='number'
                label={intl.formatMessage({ id: 'transactionFastWithdrawalFeeAmountLabel' })}
                placeholder={intl.formatMessage({
                  id: 'transactionFastWithdrawalFeeAmountLabel'
                })}
                dir='ltr'
                sx={{
                  '.MuiInputBase-root.MuiInput-root:before': {
                    borderBottom: '0px'
                  },
                  '.MuiInputBase-root.MuiInput-root:hover': {
                    borderBottom: '0px'
                  }
                }}
              />
              
              <TextField
                name={FIELDS.ADMIN_NOTE}
                value={values?.[FIELDS.ADMIN_NOTE]}
                error={touched?.[FIELDS.ADMIN_NOTE] && Boolean(errors?.[FIELDS.ADMIN_NOTE])}
                helperText={touched?.[FIELDS.ADMIN_NOTE] && errors?.[FIELDS.ADMIN_NOTE]}
                onChange={handleChange}
                onBlur={handleBlur}
                variant='outlined'
                color='warning'
                disabled={isLoading}
                fullWidth
                type='text'
                label={intl.formatMessage({
                  id: 'transactionAdminNoteLabel'
                })}
                placeholder={intl.formatMessage({
                  id: 'transactionAdminNoteLabel'
                })}
                dir='ltr'
                sx={{
                  '.MuiInputBase-root.MuiInput-root:before': {
                    borderBottom: '0px'
                  },
                  '.MuiInputBase-root.MuiInput-root:hover': {
                    borderBottom: '0px'
                  }
                }}
              />
              {calculateData?.totalTransactionAmount ? (
                <Box border='1px solid #ed6c02' borderRadius='8px' p={2} width={1}>
                  <Typography>
                    {intl.formatMessage({ id: 'totalTransactionAmount' })}: <b>{intl.formatNumber(calculateData?.totalTransactionAmount)}₪</b>
                  </Typography>
                  {calculateData?.feeAmount ? (
                    <Typography>
                      {intl.formatMessage({ id: 'transactionFeeAmount' })}: {intl.formatNumber(calculateData?.feeAmount)}₪
                    </Typography>
                  ) : null}
                  {calculateData?.fastWithdrawalFeeAmount ? (
                    <Typography>
                      {intl.formatMessage({ id: 'fastWithdrawalFeeAmount' })}: {intl.formatNumber(calculateData?.fastWithdrawalFeeAmount)}₪
                    </Typography>
                  ) : null}
                  {calculateData?.adminWithdrawalFeeAmount ? (
                    <Typography>
                      {intl.formatMessage({ id: 'adminWithdrawalFeeAmountLabel' })}: {intl.formatNumber(calculateData?.adminWithdrawalFeeAmount)}₪
                    </Typography>
                  ) : null}
                  {calculateData?.isOverdraft ? (
                    <>
                      <Typography color='error' fontWeight='bold'>
                        {intl.formatMessage({ id: 'warningOverdraftAmount' })}
                      </Typography>
                      <Typography fontWeight='bold'>
                        {intl.formatMessage({ id: 'overdraftAfterTransaction' })}: -{intl.formatNumber(calculateData?.overdraftAmount)}₪
                      </Typography>
                    </>
                  ) : null}
                </Box>
              ) : null}
              {calculateData?.isOverdraft ? (
                <FormControlLabel control={<Checkbox checked={isOverdraftConfirmed} color='warning' />} label={intl.formatMessage({ id: 'overdraftConfirmation' })} onChange={() => setIsOverdraftConfirmed((prev) => !prev)} />
              ) : null}
              <Button
                size='large'
                variant='contained'
                color='warning'
                type='submit'
                fullWidth
                disabled={isCalculating || isLoading || !isValid || (!values?.adminWithdrawalFeeAmount && !values?.fastWithdrawalFeeAmount) || (calculateData?.isOverdraft && !isOverdraftConfirmed)}
              >
                {translate('serviceFormViewCreateButton')}
              </Button>
            </Box>
          </Form>
        )
      }}
    </Formik>
  )
}
